import { Ability } from '@casl/ability';
import React, { ReactNode, useMemo } from 'react';
import { ChannelPermissionsContext } from './ChannelPermissionsContext';
import { PermissionsContext, PermissionsContextValue } from './PermissionsContext';
import {
  AbilityAction,
  AbilitySubject,
  AbilityType,
  PermissionReasonCode,
  UsePermissionResponse,
  detectSubjectType,
  getChannelAbility,
  usePermissions,
} from './hooks';

interface ChannelPermissionsProviderProps {
  channelId?: number;
  children: ReactNode;
}

function checkPermission(
  action: AbilityAction,
  subject: AbilitySubject,
  channelAbility: PermissionsContextValue,
  field?: string,
): UsePermissionResponse {
  const subjectType = channelAbility.detectSubjectType(subject);
  const rules = channelAbility.rulesFor(action, subjectType, field);
  const rule = rules.find(item => item.matchesConditions(subject));
  return { enabled: !!rule && !rule.inverted, reason: rule?.reason as PermissionReasonCode };
}

export function ChannelPermissionsProvider({
  channelId,
  children,
}: ChannelPermissionsProviderProps) {
  const caslAbility = usePermissions();
  const ability = useMemo(
    () => new Ability<AbilityType>(caslAbility, { detectSubjectType }),
    [caslAbility],
  );
  const channelAbility = useMemo(() => {
    return channelId ? getChannelAbility(ability, channelId) : ability;
  }, [ability, channelId]);

  const createCardPermission = checkPermission('create', 'Card', channelAbility);
  const updateCardPermission = checkPermission('update', 'Card', channelAbility);
  const deleteCardPermission = checkPermission('delete', 'Card', channelAbility);
  const readThreadPermission = checkPermission('read', 'ThreadMessage', channelAbility);
  const createThreadPermission = checkPermission('create', 'ThreadMessage', channelAbility);

  return (
    <ChannelPermissionsContext.Provider
      value={{
        channelId,
        channelAbility,
        canCreateCard: createCardPermission,
        canUpdateCard: updateCardPermission,
        canDeleteCard: deleteCardPermission,
        canReadThread: readThreadPermission,
        canCreateThread: createThreadPermission,
      }}
    >
      <PermissionsContext.Provider value={ability}>{children}</PermissionsContext.Provider>
    </ChannelPermissionsContext.Provider>
  );
}
